import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@/components/DatePicker';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Helper from '@/lib/helper';
import { Asterisk, Check, ChevronsUpDown, RefreshCcw, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { IncentiveSettings } from '@/types/IncentiveSettings';
import type { PropertyListings } from '@/types/PropertyListings';
import { cn } from '@/lib/utils';

interface UploadedFile {
    file: File;
    preview: string;
    type: string;
}

interface ClaimForm {
    listingId: number | null;
    date: Date;
    renName: string;
    lofNo: string;
    leaderName: string;
    type: number;
    commAmount: string;
    sstPaidBy: number | null;
    sstAmount: string;
    amount: string;
    propertyAddress: string;
    balanceReturnTo: string;
    fullName: string;
    coAgencyName: string;
    tenancyPreparedBy: string;
    referral: string;
    submittedBy: string;
    acknowledgedBy: string;
    rentMonths?: number | null;
    commissionPercentage?: number | null;
}

interface SplitAgent {
    agentId: string;
    agentName: string;
    avatar: string;
    percentage: number;
    isNew?: boolean;
    renNo: string;
    editable: boolean;
}

interface UplineSplit {
    level: number;
    agents: SplitAgent[];
}

interface SearchAgent {
    id: string;
    firstName: string;
    lastName: string;
    avatar: string;
    renNo: string;
}

const fileUploadFields = [
    { key: 'tenancyAgreement', label: 'Tenancy Agreement / Sales & Purchase Agreement', required: true  },
    { key: 'agreementToRent', label: 'Agreement To Rent / To Purchase', required: true  },
    { key: 'authorizationToLet', label: 'Authorization To Let / To Sell', required: false },
    { key: 'ssm', label: 'SSM / IC Copy', required: false },
    { key: 'form49', label: 'Form 49 / IC Copy', required: false },
    { key: 'invoiceFromAgent', label: 'Invoice From Agent', required: true  },
    { key: 'invoiceFromAgency', label: 'Invoice From Agency', required: true  },
    { key: 'paymentSlip', label: 'Payment Slip', required: true  },
];

const SST_RATE_PERCENT = 8;

const ClaimForm = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const navigate = useNavigate();
    const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [mlmUpline, setMlmUpline] = useState<any>(null);
    const [form, setForm] = useState<ClaimForm>({
        listingId: null,
        date: new Date(),
        renName: agent ? (agent.firstName + ' ' + agent.lastName) : '',
        lofNo: '',
        leaderName: agent.leader ? `${agent.leader.firstName} ${agent.leader.lastName}` : '',
        type: 1,
        sstPaidBy: null,
        amount: '0.00',
        commAmount: '0.00',
        sstAmount: '0.00',
        propertyAddress: '',
        balanceReturnTo: '',
        fullName: '',
        coAgencyName: '',
        tenancyPreparedBy: '',
        referral: '',
        submittedBy: agent ? `${agent.renNo}` : '',
        acknowledgedBy: '',
        rentMonths: null,
        commissionPercentage: null,
    });
    const [params, setParams] = useState<any>(null);
    const [agentIncentiveSettings, setAgentIncentiveSettings] = useState<IncentiveSettings[] | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [uplineSplits, setUplineSplits] = useState<UplineSplit[]>([]);
    const [searchAgents, setSearchAgents] = useState<SearchAgent[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [popSearchQuery, setPopSearchQuery] = useState<string>('');
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [selectedLevelIndex, setSelectedLevelIndex] = useState<number | null>(null);
    const [selectedAgentIndex, setSelectedAgentIndex] = useState<number | null>(null);
    const [selectedListing, setSelectedListing] = useState<PropertyListings | null>(null);
    const [listings, setListings] = useState<PropertyListings[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: UploadedFile[] }>({});

    // ─── File handlers ────────────────────────────────────────────────────────
    const handleFileUpload = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newFiles: UploadedFile[] = Array.from(files).map(file => {
            const fileType = file.type;
            let preview = '';
            if (fileType.startsWith('image/')) preview = URL.createObjectURL(file);
            else if (fileType === 'application/pdf') preview = 'pdf';
            else preview = 'document';
            return { file, preview, type: fileType };
        });

        setUploadedFiles(prev => ({ ...prev, [key]: [...(prev[key] || []), ...newFiles] }));

        if (validationErrors.includes(key)) {
            setValidationErrors(validationErrors.filter(err => err !== key));
        }
        e.target.value = '';
    };

    const handleRemoveFile = (key: string, fileIndex: number) => {
        setUploadedFiles(prev => {
            const updated = [...(prev[key] || [])];
            const removed = updated.splice(fileIndex, 1)[0];
            if (removed?.preview?.startsWith('blob:')) URL.revokeObjectURL(removed.preview);
            return { ...prev, [key]: updated };
        });
    };

    const renderFilePreview = (key: string) => {
        const files = uploadedFiles[key] || [];
        if (files.length === 0) return null;
        return (
            <div className="mt-2 flex flex-wrap gap-2">
                {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-muted/30 px-3 py-2 rounded-lg border">
                        {f.type.startsWith('image/') && (
                            <img src={f.preview} alt="Preview" className="w-10 h-10 object-cover rounded border" />
                        )}
                        {f.preview === 'pdf' && (
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" />
                            </svg>
                        )}
                        {f.preview === 'document' && (
                            <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                        )}
                        <span className="text-sm text-muted-foreground truncate max-w-[140px]">{f.file.name}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveFile(key, i)} className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive ml-1 flex-shrink-0">
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                ))}
            </div>
        );
    };

    const handleGoBack = () => navigate(-1);
    const hasError = (field: string) => validationErrors.includes(field);

    const calculateCommission = (amount: string, type: number, commPercentage?: number | null, rentMonths?: number | null): string => {
        if (!amount || amount === '0.00') return '0.00';
        const numAmount = parseFloat(amount.replace(/,/g, ''));
        if (isNaN(numAmount)) return '0.00';
        let commissionAmount;
        if (type === 1) {
            const percentage = commPercentage !== null && commPercentage !== undefined ? commPercentage : 2;
            commissionAmount = (numAmount * percentage) / 100;
        } else {
            const months = rentMonths !== null && rentMonths !== undefined ? rentMonths : 1;
            commissionAmount = numAmount * months;
        }
        return commissionAmount.toFixed(2);
    };

    const handleChange = (field: keyof ClaimForm, value: any) => {
        let updatedForm = { ...form, [field]: value };

        if (field === 'type' && value === 1) updatedForm.commissionPercentage = 2;
        if (field === 'type' && value === 2) updatedForm.commissionPercentage = 6;

        if (field === 'amount' || field === 'commissionPercentage' || field === 'rentMonths') {
            const numAmount = parseFloat(updatedForm.amount.replace(/,/g, ''));
            if (!isNaN(numAmount)) {
                const baseCommission = parseFloat(calculateCommission(updatedForm.amount, updatedForm.type, updatedForm.commissionPercentage, updatedForm.rentMonths));
                const sstRate = updatedForm.type === 1 ? 0.08 : 0.08;
                if (updatedForm.sstPaidBy === 1) {
                    const sstAmount = (baseCommission * sstRate).toFixed(2);
                    updatedForm.sstAmount = sstAmount;
                    updatedForm.commAmount = (baseCommission + parseFloat(sstAmount)).toFixed(2);
                } else if (updatedForm.sstPaidBy === 2) {
                    const divisor = updatedForm.type === 1 ? 108 : 108;
                    const actualCommission = (baseCommission * 100) / divisor;
                    const sstAmount = (baseCommission - actualCommission).toFixed(2);
                    updatedForm.sstAmount = sstAmount;
                    updatedForm.commAmount = actualCommission.toFixed(2);
                } else {
                    updatedForm.sstAmount = '0.00';
                    updatedForm.commAmount = baseCommission.toFixed(2);
                }
            } else {
                updatedForm.sstAmount = '0.00';
                updatedForm.commAmount = '0.00';
            }
        }

        if (field === 'sstPaidBy') {
            const baseCommission = Number(calculateCommission(updatedForm.amount, updatedForm.type, updatedForm.commissionPercentage, updatedForm.rentMonths));
            const sstRate = updatedForm.type === 1 ? 0.08 : 0.08;
            if (value === 1) {
                const sstAmount = (baseCommission * sstRate).toFixed(2);
                updatedForm.sstAmount = sstAmount;
                updatedForm.commAmount = (baseCommission + parseFloat(sstAmount)).toFixed(2);
            } else if (value === 2) {
                const divisor = updatedForm.type === 1 ? 108 : 108;
                const actualCommission = (baseCommission * 100) / divisor;
                const sstAmount = (baseCommission - actualCommission).toFixed(2);
                updatedForm.sstAmount = sstAmount;
                updatedForm.commAmount = actualCommission.toFixed(2);
            } else {
                updatedForm.sstAmount = '0.00';
                updatedForm.commAmount = baseCommission.toFixed(2);
            }
        }

        if (field === 'type') {
            const baseCommission = Number(calculateCommission(updatedForm.amount, value, updatedForm.commissionPercentage, updatedForm.rentMonths));
            const sstRate = value === 1 ? 0.08 : 0.08;
            if (updatedForm.sstPaidBy === 1) {
                const sstAmount = (baseCommission * sstRate).toFixed(2);
                updatedForm.sstAmount = sstAmount;
                updatedForm.commAmount = (baseCommission + parseFloat(sstAmount)).toFixed(2);
            } else if (updatedForm.sstPaidBy === 2) {
                const divisor = value === 1 ? 108 : 108;
                const actualCommission = (baseCommission * 100) / divisor;
                const sstAmount = (baseCommission - actualCommission).toFixed(2);
                updatedForm.sstAmount = sstAmount;
                updatedForm.commAmount = actualCommission.toFixed(2);
            } else {
                updatedForm.sstAmount = '0.00';
                updatedForm.commAmount = baseCommission.toFixed(2);
            }
            initializeUplineSplits();
        }

        setForm(updatedForm);
        if (validationErrors.includes(field)) {
            setValidationErrors(validationErrors.filter(err => err !== field));
        }
    };

    const initializeUplineSplits = () => {
        if (!agentIncentiveSettings || !agent) return;
        const splits: UplineSplit[] = [];
        const myIncentive = agentIncentiveSettings.find(setting => setting.gen === 1);
        if (myIncentive) {
            splits.push({ level: 1, agents: [{ agentId: agent.id, agentName: `${agent.firstName} ${agent.lastName}`, avatar: agent.avatar, percentage: myIncentive.percentage, renNo: agent.renNo, editable: false }] });
        }
        if (mlmUpline) {
            mlmUpline.forEach((upline: any, index: number) => {
                const incentive = agentIncentiveSettings.find(setting => setting.gen === (index + 2));
                splits.push({ level: index + 2, agents: [{ agentId: upline.id, agentName: `${upline.firstName} ${upline.lastName}`, avatar: upline.avatar, percentage: incentive ? incentive.percentage : 0, renNo: upline.renNo, editable: false }] });
            });
        }
        setUplineSplits(splits);
    };

    const updateAgentPercentage = (levelIndex: number, agentIndex: number, newValue: string) => {
        const newSplits = [...uplineSplits];
        const split = newSplits[levelIndex];
        const ag = split.agents[agentIndex];
        const digits = newValue.replace(/\D/g, '');
        const numericValue = digits.length === 0 ? '0.00' : (parseInt(digits, 10) / 100).toFixed(2);
        const newPercentage = parseFloat(numericValue);
        const otherTotal = split.agents.filter((_, idx) => idx !== agentIndex).reduce((sum, a) => sum + a.percentage, 0);
        if (otherTotal + newPercentage <= 100) {
            ag.percentage = newPercentage;
            setUplineSplits(newSplits);
        } else {
            toast.error(`Total percentage cannot exceed 100%. Current total: ${(otherTotal + newPercentage).toFixed(2)}%`);
        }
    };

    const getTotalPercentage = (levelIndex: number) => uplineSplits[levelIndex].agents.reduce((sum, a) => sum + a.percentage, 0);
    const isPercentageValid = (levelIndex: number) => {
        const split = uplineSplits[levelIndex];
        const total = getTotalPercentage(levelIndex);
        if (split.agents.length > 1) return split.agents.every(a => a.percentage > 0) && total <= 100;
        return total <= 100;
    };

    const handleValidate = () => {
        const errors: string[] = [];
        if (!form.renName?.trim()) errors.push('renName');
        if (!form.amount || form.amount === '0.00') errors.push('amount');
        if (!form.commAmount || form.commAmount === '0.00') errors.push('commAmount');
        if (!form.sstPaidBy) errors.push('sstPaidBy');
        // Forfeited cases are not charged SST, so a zero SST amount is expected.
        if (form.sstPaidBy !== 3 && (!form.sstAmount || form.sstAmount === '0.00')) errors.push('sstAmount');
        if (!form.propertyAddress?.trim()) errors.push('propertyAddress');
        if (form.type === 1) { if (!form.commissionPercentage) errors.push('commissionPercentage'); }
        else { if (!form.rentMonths) errors.push('rentMonths'); }

        // Only validate required fields — skip optional ones
        fileUploadFields.forEach(field => {
            if (field.required && (!uploadedFiles[field.key] || uploadedFiles[field.key].length === 0)) {
                errors.push(field.key);
            }
        });

        uplineSplits.forEach((split, index) => {
            if (!isPercentageValid(index)) errors.push(`upline-${index}`);
            split.agents.forEach((ag, agentIndex) => {
                if (!ag.agentId || ag.agentId === '') errors.push(`upline-${index}-agent-${agentIndex}`);
            });
        });

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const getAgentIncentiveCategory = async () => {
        try {
            const response = await api.get(`/me/incentives/category`);
            if (!response.data.status) throw new Error(response.data.error);
            setAgentIncentiveSettings(response.data.incentiveSettings);
        } catch (err: any) { toast.error(Helper.errorHandler(err)); }
    };

    const getAgentGenerationTree = async () => {
        try {
            const response = await api.get(`/me/tree`);
            if (!response.data.status) throw new Error(response.data.error);
            setMlmUpline(response.data.mlmUpline);
        } catch (err: any) { toast.error(Helper.errorHandler(err)); }
    };

    const searchAgentsByQuery = async (query: string) => {
        if (query.length < 3) { setSearchAgents([]); return; }
        try {
            setSearchLoading(true);
            const response = await api.get(`/agents?search=${encodeURIComponent(query)}`);
            if (!response.data.status) throw new Error(response.data.error);
            setSearchAgents(response.data.agents || []);
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
            setSearchAgents([]);
        } finally { setSearchLoading(false); }
    };

    const handleSelectAgent = (selectedAgent: SearchAgent) => {
        if (selectedLevelIndex === null || selectedAgentIndex === null) return;
        const agentExists = uplineSplits.some(split => split.agents.some(a => a.agentId === selectedAgent.id));
        if (agentExists) { toast.error("This agent is already selected in another split."); return; }
        const newSplits = [...uplineSplits];
        newSplits[selectedLevelIndex].agents[selectedAgentIndex] = { ...newSplits[selectedLevelIndex].agents[selectedAgentIndex], agentId: selectedAgent.id, agentName: `${selectedAgent.firstName} ${selectedAgent.lastName}`, avatar: selectedAgent.avatar, renNo: selectedAgent.renNo, isNew: false, editable: true };
        setUplineSplits(newSplits);
        setSearchQuery('');
        setSearchAgents([]);
        setSelectedLevelIndex(null);
        setSelectedAgentIndex(null);
    };

    const openAgentSearch = (levelIndex: number, agentIndex: number) => {
        setSelectedLevelIndex(levelIndex);
        setSelectedAgentIndex(agentIndex);
    };

    const validateForm = () => {
        if (!handleValidate()) { toast.error('Please complete all required fields.'); return; }
        setOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (value instanceof Date) formData.append(key, value.toISOString().split('T')[0]);
                else formData.append(key, value?.toString() || '');
            });
            formData.append('uplineSplits', JSON.stringify(uplineSplits));
            fileUploadFields.forEach(field => {
                (uploadedFiles[field.key] || []).forEach(f => formData.append(field.key, f.file));
            });
            const response = await api.post(`/me/claims`, formData);
            if (!response.data.status) throw new Error(response.data.error);
            toast.success('Successfully submitted!');
            navigate('/claims');
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally { setOpen(false); }
    };

    const handleSelectListing = async (listing: PropertyListings) => {
        setSelectedListing(listing);
        const percentage = agentIncentiveSettings?.find(x => x.gen === 1)?.percentage;
        if (!percentage) return;
        setForm({ ...form, listingId: listing.id, propertyAddress: listing.rawAddress || '', amount: String(listing.price), sstAmount: String((listing.price * 0.08).toFixed(2)), type: listing.forSaleOrRentId, commissionPercentage: listing.forSaleOrRentId === 1 ? 2 : null, rentMonths: listing.forSaleOrRentId === 2 ? (params?.advanceRentalMonths || 1) : null });
        setPopoverOpen(false);

        const splits: UplineSplit[] = [];
        const myIncentive = agentIncentiveSettings?.find(s => s.gen === 1);
        if (myIncentive) splits.push({ level: 1, agents: [{ agentId: agent.id, agentName: `${agent.firstName} ${agent.lastName}`, avatar: agent.avatar, percentage: myIncentive.percentage, renNo: agent.renNo, editable: false }] });
        if (mlmUpline && Array.isArray(mlmUpline)) {
            mlmUpline.forEach((upline: any, index: number) => {
                const incentive = agentIncentiveSettings?.find(s => s.gen === (index + 2));
                splits.push({ level: index + 2, agents: [{ agentId: upline.id, agentName: `${upline.firstName} ${upline.lastName}`, avatar: upline.avatar, percentage: incentive ? incentive.percentage : 0, renNo: upline.renNo, editable: false }] });
            });
        }
        if (listing.sharedAgents?.length > 0) {
            listing.sharedAgents.forEach((sharedAgent: any) => {
                const si = sharedAgent.incentiveSettings.find((s: IncentiveSettings) => s.gen === 1);
                const existing = splits.find(s => s.level === 1);
                if (existing && !existing.agents.some(a => a.agentId === sharedAgent.agent.id)) {
                    existing.agents.push({ agentId: sharedAgent.agent.id, agentName: `${sharedAgent.agent.firstName} ${sharedAgent.agent.lastName}`, avatar: sharedAgent.agent.avatar, percentage: si.percentage, renNo: sharedAgent.agent.renNo, editable: false });
                }
                sharedAgent.agent.mlmUpline?.forEach((ua: any, ui: number) => {
                    const _si = sharedAgent.incentiveSettings.find((s: IncentiveSettings) => s.gen === ui + 2);
                    if (_si) {
                        let us = splits.find(s => s.level === ui + 2);
                        if (!us) { us = { level: ui + 2, agents: [] }; splits.push(us); }
                        if (!us.agents.some(a => a.agentId === ua.id)) us.agents.push({ agentId: ua.id, agentName: `${ua.firstName} ${ua.lastName}`, avatar: ua.avatar, percentage: _si.percentage, renNo: ua.renNo, editable: false });
                    }
                });
            });
        }
        splits.forEach(split => { const n = split.agents.length; split.agents = split.agents.map(a => ({ ...a, percentage: a.percentage / n })); });
        splits.sort((a, b) => a.level - b.level);
        setUplineSplits(splits);
    };

    const searchListings = async (query: string, listingId: (string | null) = null) => {
        try {
            setLoading(true);
            let url = `/me/listings/search`;
            if (listingId) { url += `?listingId=${listingId}`; }
            else { if (!query.trim() || query.length < 2) { setListings([]); return; } url += `?search=${encodeURIComponent(query)}&closed=1`; }
            const response = await api.get(url);
            if (!response.data.status) throw new Error(response.data.error);
            if (listingId) handleSelectListing(response.data.listings[0]);
            else setListings(response.data.listings);
        } catch (err: any) { toast.error(Helper.errorHandler(err)); }
        finally { setLoading(false); }
    };

    const getClaimInfo = async () => {
        try {
            if (!selectedListing) return;
            const response = await api.get(`/me/listings/${selectedListing.id}/documents/${selectedListing.forSaleOrRentId === 1 ? 'agreement-to-purchase' : 'agreement-to-rent'}`);
            if (!response.data.status) throw new Error(response.data.error);
            if (response.data.document?.params) {
                const json = JSON.parse(response.data.document.params);
                setParams(json);
                const advanceRentalMonths = json.advanceRentalMonths || 1;
                const commissionPercentage = json.earnestDeposit || 2;
                setForm({ ...form, listingId: selectedListing.id, propertyAddress: selectedListing.rawAddress ?? '', amount: String(selectedListing.price), commAmount: String(selectedListing.price * (selectedListing.forSaleOrRentId === 1 ? (commissionPercentage / 100) : advanceRentalMonths)), sstAmount: String((selectedListing.price * (selectedListing.forSaleOrRentId === 1 ? (commissionPercentage / 100) : advanceRentalMonths) * 0.08).toFixed(2)), rentMonths: advanceRentalMonths, commissionPercentage });
            } else {
                setParams({ advanceRentalMonths: 1 });
                toast.warning('Agreement document not found. Using default values.');
            }
        } catch (err) { toast.error(Helper.errorHandler(err)); setParams({ advanceRentalMonths: 1 }); }
    };

    useEffect(() => {
        const fetchData = async () => { await getAgentGenerationTree(); await getAgentIncentiveCategory(); };
        fetchData();
        const listingId = searchParams.get('listingId');
        if (listingId) searchListings('', listingId);
    }, []);

    useEffect(() => { if (agentIncentiveSettings) initializeUplineSplits(); }, [mlmUpline, agentIncentiveSettings, selectedListing]);

    useEffect(() => {
        const t = setTimeout(() => { if (searchQuery) searchAgentsByQuery(searchQuery); else setSearchAgents([]); }, 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        const t = setTimeout(() => { if (popSearchQuery) searchListings(popSearchQuery); }, 300);
        return () => clearTimeout(t);
    }, [popSearchQuery]);

    useEffect(() => { if (selectedListing) getClaimInfo(); }, [selectedListing]);

    useEffect(() => {
        if (form.amount && form.amount !== '0.00') {
            const numAmount = parseFloat(form.amount.replace(/,/g, ''));
            if (!isNaN(numAmount)) {
                const baseCommission = Number(calculateCommission(form.amount, form.type, form.commissionPercentage, form.rentMonths));
                const sstRate = form.type === 1 ? 0.08 : 0.08;
                let finalCommission: any = baseCommission.toFixed(2);
                let sstAmount = '0.00';
                if (form.sstPaidBy === 1) { sstAmount = (baseCommission * sstRate).toFixed(2); finalCommission = (baseCommission + parseFloat(sstAmount)).toFixed(2); }
                else if (form.sstPaidBy === 2) { const d = form.type === 1 ? 108 : 108; const ac = (baseCommission * 100) / d; sstAmount = (baseCommission - ac).toFixed(2); finalCommission = ac.toFixed(2); }
                setForm(prev => ({ ...prev, sstAmount, commAmount: finalCommission.toString() }));
            }
        }
    }, [form.commissionPercentage, form.rentMonths, form.type, form.amount, selectedListing, form.sstPaidBy]);

    return (
        <div className="">
            <div className="print:hidden w-full md:w-96 flex">
                <Label className='mr-2'>Ref: </Label>
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" size="sm" className="justify-between text-xs overflow-hidden">
                            {selectedListing ? `${selectedListing.listingReferenceNumber} - ${selectedListing.headline}` : "Search listing..."}
                            <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput placeholder="Search listings..." value={popSearchQuery} onValueChange={setPopSearchQuery} />
                            <CommandList>
                                <CommandEmpty>No listings found.</CommandEmpty>
                                <CommandGroup>
                                    {listings && listings.map(listing => (
                                        <CommandItem key={listing.id} value={listing.id.toString()} onSelect={() => handleSelectListing(listing)}>
                                            <Check className={cn("mr-2 h-4 w-4", selectedListing?.id === listing.id ? "opacity-100" : "opacity-0")} />
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{listing.listingReferenceNumber}</div>
                                                <div className="text-xs text-muted-foreground truncate">{listing.headline}</div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>

            <h6 className='h6 text-center font-bold mt-5'>SALT & LIGHT REALTY SDN BHD CLAIM FORM</h6>

            <div className="mt-5">
                <div className='mb-5'>
                    <Label>Date:</Label>
                    <DatePicker className='mt-2' value={form.date} disabled />
                </div>
                <div className='mb-5 w-full'>
                    <Label>Type <Asterisk size={12} className="text-red-500" /></Label>
                    <div className='flex items-center gap-2 mt-2'>
                        <Button size={'sm'} variant={form.type === 1 ? 'default' : 'outline'} onClick={() => handleChange('type', 1)}>Sale</Button>
                        <Button size={'sm'} variant={form.type === 2 ? 'default' : 'outline'} onClick={() => handleChange('type', 2)}>Rent</Button>
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>REN Name (as per I/C) <Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" className={`mt-2 text-sm w-full ${hasError('renName') ? 'border-red-400' : ''}`} placeholder="Enter REN Name" value={form.renName} onChange={(e) => handleChange('renName', e.target.value)} disabled />
                        {hasError('renName') && <span className="text-xs text-red-500">REN Name is required.</span>}
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Leader Name</Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.leaderName} onChange={(e) => handleChange('leaderName', e.target.value)} disabled />
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Letter of Offer ({form.type === 1 ? 'Sale' : 'Rent'}) Number</Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.lofNo} onChange={(e) => handleChange('lofNo', e.target.value)} />
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>{form.type === 1 ? 'Sale' : 'Rent'} Amount <Asterisk size={12} className="text-red-500" /></Label>
                        <div className="relative mt-2 flex items-center gap-0">
                            <div className='flex items-center absolute left-2 shadow-none'><Badge variant={'secondary'}>RM</Badge></div>
                            <Input type="text" className={`text-sm pl-12 w-full ${hasError('amount') ? 'border-red-400' : ''}`} value={Helper.formatWithDecimal(Number(form.amount))} onFocus={Helper.handleFocusToEnd} onClick={Helper.handleFocusToEnd} onChange={(e) => { const value = Helper.handleCurrencyChange(e); handleChange('amount', value); }} />
                        </div>
                        {hasError('amount') && <span className="text-xs text-red-500">Amount is required.</span>}
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>{form.type === 1 ? 'Commission %' : 'Rent Months'} <Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" inputMode="numeric" className={`mt-2 text-sm w-full ${hasError(form.type === 1 ? 'commissionPercentage' : 'rentMonths') && 'border-red-500'}`} value={Helper.formatWithDecimal(Number(form.type === 1 ? (form.commissionPercentage || '') : (form.rentMonths || '')))} onKeyDown={Helper.allowOnlyNumbers} onFocus={Helper.handleFocusToEnd} onClick={Helper.handleFocusToEnd} onChange={(e) => { const value = Helper.handleCurrencyChange(e); if (form.type === 1) handleChange('commissionPercentage', value ? Number(value) : null); else handleChange('rentMonths', value ? Number(value) : null); }} placeholder={form.type === 1 ? 'Enter %' : 'Enter months'} />
                        {hasError(form.type === 1 ? 'commissionPercentage' : 'rentMonths') && <span className="text-xs text-red-500">{form.type === 1 ? 'Commission Percentage' : 'Rent Months'} is required.</span>}
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>{SST_RATE_PERCENT}% SST Paid By <Asterisk size={12} className="text-red-500" /></Label>
                        <div className='flex gap-2 mt-2'>
                            <Button className={`cursor-pointer ${hasError('sstPaidBy') && 'border-red-400'}`} size={'sm'} variant={form.sstPaidBy === 1 ? 'default' : 'outline'} onClick={() => handleChange('sstPaidBy', 1)}>Owner</Button>
                            <Button className={`cursor-pointer ${hasError('sstPaidBy') && 'border-red-400'}`} size={'sm'} variant={form.sstPaidBy === 2 ? 'default' : 'outline'} onClick={() => handleChange('sstPaidBy', 2)}>Agent</Button>
                            <Button className={`cursor-pointer ${hasError('sstPaidBy') && 'border-red-400'}`} size={'sm'} variant={form.sstPaidBy === 3 ? 'default' : 'outline'} onClick={() => handleChange('sstPaidBy', 3)}>Forfeited</Button>
                        </div>
                        {form.sstPaidBy === 3 && <p className="text-xs text-muted-foreground mt-2">Forfeited case — no SST is charged.</p>}
                        <div className="relative mt-2 flex items-center gap-0">
                            <div className='flex items-center absolute left-2 shadow-none'><Badge variant={'secondary'}>RM</Badge></div>
                            <Input type="text" className={`text-sm pl-12 w-full ${hasError('sstAmount') ? 'border-red-400' : ''}`} value={Helper.formatWithDecimal(Number(form.sstAmount))} disabled />
                        </div>
                        {hasError('sstAmount') && <span className="text-xs text-red-500">SST Amount is required.</span>}
                    </div>
                </div>
                <div className='mb-5'>
                    <Label>
                        Total Commission Amount
                        {(() => {
                            const amount = Number(form.amount);
                            const commissionPercentage = form.commissionPercentage ?? 2;
                            const rentMonths = form.rentMonths ?? params?.advanceRentalMonths ?? 1;
                            const baseCommission = form.type === 1 ? (amount * commissionPercentage) / 100 : amount * rentMonths;
                            const calculationText = form.type === 1 ? `${commissionPercentage}%` : `${rentMonths} month${rentMonths > 1 ? 's' : ''}`;
                            let formula = '';
                            if (form.sstPaidBy === 1) { 
                                const sa = baseCommission * (form.type === 1 ? 0.08 : 0.08); 
                                formula = ` + RM ${Helper.formatWithDecimal(sa)} SST (${SST_RATE_PERCENT}%) = RM ${Helper.formatWithDecimal(baseCommission + sa)}`; 
                            } else if (form.sstPaidBy === 2) { 
                                const d = form.type === 1 ? 108 : 108; 
                                const ac = (baseCommission * 100) / d; 
                                const sa = baseCommission - ac; 
                                formula = ` × 100/${d} = RM ${Helper.formatWithDecimal(ac)} (SST: RM ${Helper.formatWithDecimal(sa)})`;
                            } else if (form.sstPaidBy === 3) {
                                formula = ` = RM ${Helper.formatWithDecimal(baseCommission)} (forfeited — no SST)`;
                            } else {
                                formula = ` = RM ${Helper.formatWithDecimal(baseCommission)}`;
                            }
                            return <span className="text-xs text-muted-foreground ml-2 font-normal">(RM {Helper.formatWithDecimal(amount)} × {calculationText} = RM {Helper.formatWithDecimal(baseCommission)}){formula}</span>;
                        })()}
                    </Label>
                    <div className="relative mt-2 flex items-center gap-0">
                        <div className='flex items-center absolute left-2 shadow-none'><Badge variant={'secondary'}>RM</Badge></div>
                        <Input type="text" className="text-sm pl-12 w-full" value={Helper.formatWithDecimal(Number(form.commAmount))} disabled />
                    </div>
                </div>
                <div className="md:col-span-2 mb-5">
                    <Label>Property Address <Asterisk size={12} className="text-red-500" /></Label>
                    <Textarea className={`mt-2 ${hasError('propertyAddress') ? 'border-red-400' : ''}`} value={form.propertyAddress} onChange={(e) => handleChange('propertyAddress', e.target.value)} />
                    {hasError('propertyAddress') && <span className="text-xs text-red-500">Property Address is required.</span>}
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Balance of E.D / Amount Return to</Label>
                        <Select onValueChange={(val) => handleChange('balanceReturnTo', val)}>
                            <SelectTrigger className='w-full mt-2'><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Owner</SelectItem>
                                <SelectItem value="2">Purchaser</SelectItem>
                                <SelectItem value="3">Tenant</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Full Name</Label>
                        <Input className='mt-2 text-sm w-full' type="text" value={form.fullName} onChange={(e) => handleChange('fullName', e.target.value)} />
                    </div>
                </div>
                <div className='mb-5'>
                    <Label>Co-Agency Name (As per Invoice)</Label>
                    <Input className='mt-2 text-sm w-full' type="text" value={form.coAgencyName} onChange={(e) => handleChange('coAgencyName', e.target.value)} />
                </div>
                <div className='flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Tenancy Prepared By</Label>
                        <Select onValueChange={(val) => handleChange('tenancyPreparedBy', val)}>
                            <SelectTrigger className='w-full mt-2'><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Negotiator</SelectItem>
                                <SelectItem value="2">Admin</SelectItem>
                                <SelectItem value="3">Owner</SelectItem>
                                <SelectItem value="4">Tenant</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Referral (as per IC)</Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.referral} onChange={(e) => handleChange('referral', e.target.value)} />
                    </div>
                </div>

                <hr className='my-5' />
                <div className='mb-5'>
                    <Label className='text-lg font-semibold'>Commission Distribution</Label>
                    {uplineSplits.length > 0 ? (
                        <div className='mt-3 space-y-4'>
                            {uplineSplits.map((split, levelIndex) => (
                                <div key={levelIndex} className={`border rounded-lg p-4 ${hasError(`upline-${levelIndex}`) ? 'border-red-400' : ''}`}>
                                    <div className='flex items-center gap-2 mb-3'>
                                        <Badge variant='outline' className='font-mono'>L{split.level}</Badge>
                                    </div>
                                    <div className='space-y-2'>
                                        {split.agents.map((ag, agentIndex) => (
                                            <div key={agentIndex} className={`md:flex items-start gap-3 p-3 bg-gray-50 rounded-lg ${hasError(`upline-${levelIndex}-agent-${agentIndex}`) ? 'border border-red-400' : ''}`}>
                                                <div className='flex gap-3 mb-2 md:mb-0 md:flex-1'>
                                                    <Avatar className='h-10 w-10'>
                                                        <AvatarImage src={ag.avatar ? `${import.meta.env.VITE_AWS_S3}/${ag.avatar}` : ''} />
                                                        <AvatarFallback>{ag.agentName ? ag.agentName.split(' ').map(n => n[0]).join('') : '?'}</AvatarFallback>
                                                    </Avatar>
                                                    <div className='flex-1'>
                                                        {ag.isNew ? (
                                                            <Popover open={selectedLevelIndex === levelIndex && selectedAgentIndex === agentIndex} onOpenChange={(o) => { if (!o) { setSelectedLevelIndex(null); setSelectedAgentIndex(null); setSearchQuery(''); setSearchAgents([]); } }}>
                                                                <PopoverTrigger asChild>
                                                                    <Input readOnly onClick={() => openAgentSearch(levelIndex, agentIndex)} placeholder="Click to select agent" value={ag.agentName || ''} className={`cursor-pointer text-start mt-1 ${hasError(`upline-${levelIndex}-agent-${agentIndex}`) ? 'border-red-400' : ''}`} />
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-80 p-0 mt-1" align="start">
                                                                    <Command shouldFilter={false}>
                                                                        <CommandInput placeholder="Search agents (min 3 characters)..." value={searchQuery} onValueChange={setSearchQuery} />
                                                                        <CommandList>
                                                                            {searchLoading ? <div className="flex items-center justify-center p-4"><Spinner /></div>
                                                                                : searchQuery.length < 3 ? <CommandEmpty>Type at least 3 characters to search</CommandEmpty>
                                                                                    : searchAgents.length === 0 ? <CommandEmpty>No agents found</CommandEmpty>
                                                                                        : <CommandGroup>{searchAgents.map(sa => (
                                                                                            <CommandItem key={sa.id} value={`${sa.id}`} onSelect={() => handleSelectAgent(sa)} className="cursor-pointer">
                                                                                                <div className="flex items-center gap-3 w-full">
                                                                                                    <Avatar className="h-8 w-8">
                                                                                                        <AvatarImage src={sa.avatar ? `${import.meta.env.VITE_AWS_S3}/${sa.avatar}` : ''} />
                                                                                                        <AvatarFallback>{`${sa.firstName[0]}${sa.lastName[0]}`}</AvatarFallback>
                                                                                                    </Avatar>
                                                                                                    <p className="text-sm font-medium">{sa.firstName} {sa.lastName} ({sa.renNo})</p>
                                                                                                </div>
                                                                                            </CommandItem>
                                                                                        ))}</CommandGroup>
                                                                            }
                                                                        </CommandList>
                                                                    </Command>
                                                                </PopoverContent>
                                                            </Popover>
                                                        ) : (
                                                            <>
                                                                <div className='flex gap-1 items-center'>
                                                                    <p className='text-sm font-medium'>{ag.agentName || 'New Agent'}</p>
                                                                    {ag.editable && <RefreshCcw size={14} className='cursor-pointer' onClick={() => { const ns = [...uplineSplits]; ns[levelIndex].agents[agentIndex].isNew = true; setUplineSplits(ns); setSelectedLevelIndex(levelIndex); setSelectedAgentIndex(agentIndex); setSearchQuery(''); setSearchAgents([]); }} />}
                                                                </div>
                                                                <p className='text-xs'>{ag.renNo || ''}</p>
                                                                <p className='text-xs text-gray-500'>{agentIndex === 0 ? 'Direct Upline' : 'Sharing'}</p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                {
                                                    levelIndex === 0 && (
                                                        <div className='flex flex-col gap-2 md:min-w-[200px]'>
                                                            <div className='flex items-center gap-2'>
                                                                <div className='flex items-center gap-1'>
                                                                    <Input type="text" inputMode="numeric" value={ag.percentage.toFixed(2)} onKeyDown={Helper.allowOnlyNumbers} onFocus={Helper.handleFocusToEnd} onClick={Helper.handleFocusToEnd} onChange={(e) => updateAgentPercentage(levelIndex, agentIndex, e.target.value)} className="text-sm text-center w-20" maxLength={6} />
                                                                    <span className='text-sm font-medium'>%</span>
                                                                </div>
                                                                <div className='flex-1 text-right'>
                                                                    <div className='text-sm font-semibold text-primary'>
                                                                        RM {Helper.formatWithDecimal(Number(((parseFloat(form.sstPaidBy === 1 ? calculateCommission(form.amount, form.type, form.commissionPercentage, form.rentMonths) : form.commAmount.replace(/,/g, '')) * ag.percentage) / 100).toFixed(2)))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded'>
                                                                RM {Helper.formatWithDecimal(Number(form.sstPaidBy === 1 ? calculateCommission(form.amount, form.type, form.commissionPercentage, form.rentMonths) : form.commAmount.replace(/,/g, '')))} × {ag.percentage.toFixed(2)}% = RM {Helper.formatWithDecimal(Number(((parseFloat(form.sstPaidBy === 1 ? calculateCommission(form.amount, form.type, form.commissionPercentage, form.rentMonths) : form.commAmount.replace(/,/g, '')) * ag.percentage) / 100).toFixed(2)))}
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : <p className='text-sm text-gray-500 mt-2'>No upline found</p>}
                </div>
                <hr className='my-5' />
                <div className='mb-5'>
                    <Label>Submitted By (REN)</Label>
                    <Input type="text" className="mt-2 text-sm md:w-auto w-full" value={form.submittedBy} disabled />
                </div>
            </div>

            <hr className='my-5' />
            <div className="space-y-6">
                <h3 className="text-lg font-semibold">Required Documents</h3>
                {fileUploadFields.map((field, index) => (
                    <div key={field.key} className="flex items-start gap-4">
                        <div className="flex-shrink-0 text-sm">{index + 1}.</div>
                        <div className="flex-1">
                            <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                                {field.label}
                                {field.required
                                    ? <Asterisk size={12} className="text-red-500" />
                                    : <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                                }
                            </Label>
                            <Input
                                type="file"
                                multiple
                                data-key={field.key}
                                className={`text-sm max-w-md ${hasError(field.key) ? 'border-red-400' : ''}`}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => handleFileUpload(field.key, e)}
                            />
                            {renderFilePreview(field.key)}
                            {hasError(field.key) && <span className="text-xs text-red-500">At least one file is required.</span>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" className='uppercase' onClick={handleGoBack}>Back</Button>
                <Button variant="default" className='uppercase' onClick={validateForm}>Submit</Button>
            </div>

            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to proceed?</AlertDialogTitle>
                        <AlertDialogDescription>Please ensure all details are correct before confirming.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className='cursor-pointer' onClick={() => setOpen(false)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction className='cursor-pointer' onClick={handleSubmit}>
                            Confirm
                            {submitting && <Spinner />}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ClaimForm;
